import type { InboundHoldingContainer, InboundHoldingReason, InboundHoldingTask } from "./inboundApi";

const PURCHASE_ORDER_SERVICE_SKUS = new Set(["fee", "tooling", "prototype"]);

/** FEE, TOOLING, and PROTOTYPE are purchased as a service line. The purchase order description names the thing. */
export function isPurchaseOrderServiceSku(sku: string | null | undefined): boolean {
  return PURCHASE_ORDER_SERVICE_SKUS.has((sku ?? "").trim().toLowerCase());
}

export type HoldingQueueCard = {
  key: string;
  task: InboundHoldingTask;
  lines: InboundHoldingTask[];
  focusContainer: InboundHoldingContainer | null;
  reasons: InboundHoldingReason[];
  notes: string[];
};

function uniqueHoldReasons(tasks: InboundHoldingTask[]): InboundHoldingReason[] {
  const reasons: InboundHoldingReason[] = [];
  for (const task of tasks) {
    if (!reasons.includes(task.reason)) reasons.push(task.reason);
  }
  return reasons;
}

const INVOICE_HOLD_REASONS = new Set<InboundHoldingTask["reason"]>([
  "waiting_on_invoice",
  "invoice_receipt_mismatch",
  "purchase_order_short",
  "manufacturer_part_mismatch",
]);

const BLOCKING_HOLD_STATUSES = new Set<InboundHoldingTask["status"]>([
  "open",
  "mismatch",
  "certified_mismatch",
  "deferred",
]);

export type HoldingBlocker =
  | { kind: "reason"; reason: InboundHoldingReason }
  | { kind: "megaventory_purchase_invoice" };

function isInvoiceHoldingTask(task: InboundHoldingTask): boolean {
  return INVOICE_HOLD_REASONS.has(task.reason);
}

function holdingLinesOnSameReceiptItems(
  lines: readonly InboundHoldingTask[],
  all: readonly InboundHoldingTask[],
): InboundHoldingTask[] {
  const itemIds = new Set(lines.map((line) => line.inbound_receipt_item_id));
  const related = all.filter((task) => itemIds.has(task.inbound_receipt_item_id));
  return related.length > 0 ? related : [...lines];
}

/**
 * Holds that still block Release to To stock.
 * A finished-goods hold is the release itself, so it is listed only when another hold is still open.
 * A verified invoice hold still blocks until the purchase invoice is in Megaventory.
 */
export function holdingBlockersForLines(
  lines: readonly InboundHoldingTask[],
  all: readonly InboundHoldingTask[],
): HoldingBlocker[] {
  const related = holdingLinesOnSameReceiptItems(lines, all);
  const blockers: HoldingBlocker[] = [];
  const seenReasons = new Set<InboundHoldingReason>();
  let waitingOnMegaventory = false;
  for (const task of related) {
    const stillOpen = BLOCKING_HOLD_STATUSES.has(task.status) && task.reason !== "awaiting_wofg";
    if (stillOpen && !seenReasons.has(task.reason)) {
      seenReasons.add(task.reason);
      blockers.push({ kind: "reason", reason: task.reason });
    }
    if (
      isInvoiceHoldingTask(task)
      && task.status === "verified"
      && task.purchase_invoice_in_megaventory !== true
    ) {
      waitingOnMegaventory = true;
    }
  }
  if (waitingOnMegaventory) {
    blockers.push({ kind: "megaventory_purchase_invoice" });
  }
  const finishedGoodsOpen = related.some(
    (task) => task.reason === "awaiting_wofg" && BLOCKING_HOLD_STATUSES.has(task.status),
  );
  if (finishedGoodsOpen && blockers.length > 0 && !seenReasons.has("awaiting_wofg")) {
    blockers.push({ kind: "reason", reason: "awaiting_wofg" });
  }
  return blockers;
}

/** Release is available only when no hold is still blocking. */
export function canReleaseHoldingLines(
  lines: readonly InboundHoldingTask[],
  all: readonly InboundHoldingTask[],
): boolean {
  if (holdingBlockersForLines(lines, all).length > 0) return false;
  const related = holdingLinesOnSameReceiptItems(lines, all);
  return related.some(
    (task) =>
      (isInvoiceHoldingTask(task) && task.status === "verified" && task.purchase_invoice_in_megaventory === true)
      || isReleaseReadyFinishedGoodsHold(task),
  );
}

function isReleaseReadyFinishedGoodsHold(task: InboundHoldingTask): boolean {
  return task.reason === "awaiting_wofg"
    && (task.status === "open" || task.status === "mismatch" || task.status === "verified");
}

export function holdingReleaseTaskIds(
  lines: readonly InboundHoldingTask[],
  all: readonly InboundHoldingTask[],
): string[] {
  if (!canReleaseHoldingLines(lines, all)) return [];
  const related = holdingLinesOnSameReceiptItems(lines, all);
  const invoiceIds = related
    .filter((task) => isInvoiceHoldingTask(task) && task.status === "verified")
    .map((task) => task.id);
  if (invoiceIds.length > 0) return invoiceIds;
  return related
    .filter(isReleaseReadyFinishedGoodsHold)
    .map((task) => task.id);
}

function holdingQueueStatusRank(task: InboundHoldingTask): number {
  const blocksRelease = BLOCKING_HOLD_STATUSES.has(task.status) && task.reason !== "awaiting_wofg";
  if (blocksRelease && task.reason !== "waiting_on_invoice") return 3;
  if (task.reason === "awaiting_wofg" && BLOCKING_HOLD_STATUSES.has(task.status)) return 2;
  if (blocksRelease) return 2;
  return 0;
}

function mostBlockingHoldingTask(tasks: InboundHoldingTask[]): InboundHoldingTask {
  return [...tasks].sort((left, right) => {
    const rank = holdingQueueStatusRank(right) - holdingQueueStatusRank(left);
    if (rank !== 0) return rank;
    return left.created_at.localeCompare(right.created_at);
  })[0];
}

/** Card keys for one hold. Each printed inventory carton is its own card. Two holds on the same carton share a card. */
export function holdingInventoryCartonKeys(task: InboundHoldingTask): string[] {
  const cartonIds = [
    ...new Set(
      (task.containers ?? [])
        .map((container) => container.id.trim())
        .filter((id) => id !== ""),
    ),
  ];
  if (cartonIds.length > 0) return cartonIds.map((id) => `carton:${id}`);
  return [`task:${task.id}`];
}

/** One queue card per inventory carton. Holds that name the same carton stay together. */
export function groupHoldingTasksByInventoryCarton(
  filtered: InboundHoldingTask[],
  all: InboundHoldingTask[],
): HoldingQueueCard[] {
  const cards: HoldingQueueCard[] = [];
  const seen = new Set<string>();
  for (const task of filtered) {
    for (const key of holdingInventoryCartonKeys(task)) {
      if (seen.has(key)) continue;
      seen.add(key);
      const related = all.filter((candidate) => holdingInventoryCartonKeys(candidate).includes(key));
      const group = related.length > 0 ? related : [task];
      const notes: string[] = [];
      for (const relatedTask of group) {
        const note = relatedTask.notes?.trim() ?? "";
        if (note !== "" && !notes.includes(note)) notes.push(note);
      }
      const focusContainer =
        group
          .flatMap((line) => line.containers ?? [])
          .find((container) => `carton:${container.id}` === key) ?? null;
      cards.push({
        key,
        task: mostBlockingHoldingTask(group),
        lines: group,
        focusContainer,
        reasons: uniqueHoldReasons(group),
        notes,
      });
    }
  }
  return cards;
}
